import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

export function CreateBookingModal({ onClose, onSuccess }) {
  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    selectedPackage: null,
    selectedColor: null,
    startDate: '',
    endDate: '',
    status: 'confirmed',
    note: ''
  });

  // Product data
  const [products, setProducts] = useState([]);
  const [colors, setColors] = useState([]);
  const [variantMapping, setVariantMapping] = useState({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await api.getProducts();
        setProducts(response.products || []);
        setColors(response.colors || []);
        setVariantMapping(response.variantMapping || {});
      } catch (error) {
        console.error('Error loading products:', error);
        toast.error('Fehler beim Laden der Produkte');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Check availability when package/color/date changes
  useEffect(() => {
    if (!formData.selectedPackage || !formData.selectedColor || !formData.startDate) {
      setAvailability(null);
      return;
    }

    const variantKey = `${formData.selectedPackage}-${formData.selectedColor}`;
    const variantData = variantMapping[variantKey];

    if (!variantData) {
      setAvailability(null);
      return;
    }

    const checkAvail = async () => {
      try {
        setCheckingAvailability(true);
        const result = await api.checkAvailability(variantData.gid, formData.startDate);
        setAvailability(result);
      } catch (error) {
        console.error('Availability check failed:', error);
        setAvailability({ available: false, error: true });
      } finally {
        setCheckingAvailability(false);
      }
    };

    checkAvail();
  }, [formData.selectedPackage, formData.selectedColor, formData.startDate, variantMapping]);

  // Calculate total days
  const totalDays = useMemo(() => {
    if (!formData.startDate) return 1;
    if (!formData.endDate) return 1;

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return diffDays;
  }, [formData.startDate, formData.endDate]);

  // Form validation
  const isFormValid = useMemo(() => {
    if (!formData.customerName?.trim()) return false;
    if (!formData.customerEmail?.trim()) return false;
    if (!formData.selectedPackage) return false;
    if (!formData.selectedColor) return false;
    if (!formData.startDate) return false;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) return false;

    // Date validation (end date must be >= start date)
    if (formData.endDate && formData.endDate < formData.startDate) return false;

    // STRICT availability check
    if (availability && !availability.available) return false;

    return true;
  }, [formData, availability]);

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    setSubmitting(true);
    try {
      const variantKey = `${formData.selectedPackage}-${formData.selectedColor}`;
      const variantData = variantMapping[variantKey];
      const selectedPackageData = products.find(p => p.id === formData.selectedPackage);
      const selectedColorData = colors.find(c => c.id === formData.selectedColor);

      const bookingData = {
        variantGid: variantData.gid,
        productTitle: selectedPackageData.name,
        variantTitle: selectedColorData.name,
        customerEmail: formData.customerEmail.trim(),
        customerName: formData.customerName.trim(),
        eventDate: formData.startDate,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        totalDays: totalDays,
        status: formData.status,
        note: formData.note?.trim() || undefined,
        orderId: `MANUAL-${Date.now()}`
      };

      await api.createBooking(bookingData);

      toast.success('Buchung erfolgreich erstellt!');
      onSuccess();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.message || 'Fehler beim Erstellen der Buchung');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div className="text-gray-500">Lade Produkte...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Neue Buchung erstellen
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '4px 8px'
            }}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Customer Details */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Kundendaten
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Max Mustermann"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  E-Mail *
                </label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  placeholder="max@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Package Selection */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Paket auswählen *
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {products.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => setFormData({ ...formData, selectedPackage: pkg.id })}
                  style={{
                    border: `2px solid ${formData.selectedPackage === pkg.id ? '#3b82f6' : '#E5E7EB'}`,
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    background: formData.selectedPackage === pkg.id ? '#EFF6FF' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>{pkg.name}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6', marginBottom: '8px' }}>
                    {pkg.price}€
                  </div>
                  <ul style={{ fontSize: '12px', color: '#6B7280', paddingLeft: '20px', margin: 0 }}>
                    {pkg.features?.slice(0, 3).map((feature, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Color Selection */}
          {formData.selectedPackage && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                Farbe auswählen *
              </h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {colors.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, selectedColor: color.id })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      border: `2px solid ${formData.selectedColor === color.id ? '#3b82f6' : '#E5E7EB'}`,
                      borderRadius: '24px',
                      background: formData.selectedColor === color.id ? '#EFF6FF' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: color.hex,
                      border: '2px solid #E5E7EB'
                    }} />
                    {color.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date Selection */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Datum auswählen *
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Startdatum *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Enddatum (optional)
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  min={formData.startDate}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{
                padding: '8px 16px',
                background: '#F3F4F6',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                {totalDays} {totalDays === 1 ? 'Tag' : 'Tage'}
              </div>
            </div>
          </div>

          {/* Availability Check */}
          {formData.selectedPackage && formData.selectedColor && formData.startDate && (
            <div style={{ marginBottom: '24px' }}>
              {checkingAvailability ? (
                <div style={{
                  padding: '12px',
                  background: '#F3F4F6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#6B7280'
                }}>
                  Prüfe Verfügbarkeit...
                </div>
              ) : availability ? (
                <div style={{
                  padding: '12px',
                  background: availability.available ? '#D1FAE5' : '#FEE2E2',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: availability.available ? '#065F46' : '#991B1B',
                  fontWeight: '500'
                }}>
                  {availability.available ? (
                    <>
                      ✓ Verfügbar ({availability.availableCount} von {availability.totalInventory} Einheiten frei)
                    </>
                  ) : (
                    <>
                      ✗ Keine Kapazität verfügbar (0 von {availability.totalInventory || 0} Einheiten frei)
                    </>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* Booking Details */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              Buchungsdetails
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="pending">Ausstehend (Pending)</option>
                  <option value="confirmed">Bestätigt (Confirmed)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                  Notiz (optional)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="z.B. Telefonbuchung, Stammkunde, Event-Details..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '24px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                background: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                opacity: submitting ? 0.5 : 1
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!isFormValid || submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: isFormValid && !submitting ? '#3b82f6' : '#D1D5DB',
                color: 'white',
                cursor: isFormValid && !submitting ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {submitting ? 'Erstelle...' : 'Buchung erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
